import React, { useEffect, useMemo, useState } from 'react';
import { Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import { AddressInput } from '../../../../../common/Form/AddressInput/AddressInput';
import { PhoneNumberInput } from '../../../../../common/Form/PhoneNumberInput/PhoneNumberInput';
import { Input } from '../../../../../common/Form/Input/Input';
import { maxMobileSize, minSymbolQTY, phoneTypes, validationMessages } from '../../../../../../utils/constants';
import styles from './EditPersonalDetails.module.scss';
import { usePrevious, useWindowSize } from '../../../../../../utils/hooks';
import BaseModal from '../../../../../common/ModalWindows/BaseModal';
import ValidationErrorLabel from '../../../../../common/ValidationErrorLabel/ValidationErrorLabel';
import { emailRegExp } from '../../../../../../utils/regexps';
import api from '../../../../../../api/apiSettings';
import colors from '../../../../../../scss/colors';

function EditPersonalDetails(props) {
  const { data, state, changeState, updateUserData, updating } = props;

  const prevUpdating = usePrevious(updating);

  const size = useWindowSize();

  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const {
    FirstName,
    LastName,
    AddressLine1,
    AddressLine2,
    SecondaryEmail,
    Phone,
    PhoneType,
    PhoneCountryCode,
    Country,
    State,
    City
  } = data || {};

  const [newData, setNewData] = useState({
    FirstName,
    LastName,
    SecondaryEmail,
    Phone,
    PhoneCountryCode,
    PhoneType,
    AddressLine1,
    AddressLine2,
    Country,
    State,
    City
  });

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    country: false,
    city: false,
    email: false,
    customError: false
  });
  const [phone, setPhone] = useState(`${newData.PhoneCountryCode}${newData.Phone}`);

  const validateForm = () => {
    setErrors({
      firstName: !newData.FirstName,
      lastName: !newData.LastName,
      phone: !newData.Phone,
      country: !newData.Country,
      city: !newData.City,
      email: !!newData.SecondaryEmail?.length && !newData.SecondaryEmail.match(emailRegExp)?.length
    });
  };

  const clickSave = async () => {
    if (Object.values(errors).some((value) => value)) return;

    const countryFromApi = await api.get(`v1/get-countries?keyword=${newData.Country}`);
    if (newData.Country !== countryFromApi.data?.Data?.[0]?.Name) {
      setErrors({
        ...errors,
        customError: validationMessage?.VALIDATION_LOCATION_COUNTRY
      });
      return;
    }

    if (newData.State) {
      const stateFromApi = await api.get(
        `v1/get-states-by-country?countryName=${newData.Country}&keyword=${newData.State}`
      );
      if (newData.State !== stateFromApi.data?.Data?.[0]?.Name) {
        setErrors({
          ...errors,
          customError: validationMessage?.VALIDATION_LOCATION_STATE
        });
        return;
      }
    }

    const cityFromApi = await api.get(
      `v1/get-cities-by-state?countryName=${newData.Country}&stataName=${newData.State}&keyword=${newData.City}`
    );
    if (newData.City !== cityFromApi.data?.Data?.[0]?.Name) {
      setErrors({
        ...errors,
        customError: validationMessage.VALIDATION_LOCATION_CITY
      });
      return;
    }

    updateUserData({ ...newData });
  };

  useEffect(() => {
    validateForm();
  }, [newData.FirstName, newData.LastName, newData.Phone, newData.Country, newData.City, newData.SecondaryEmail]);

  useEffect(() => {
    if (prevUpdating === true && updating === false) changeState(false);
  }, [updating]);

  return (
    <BaseModal
      open={state}
      fullscreen={size.width < maxMobileSize}
      closeHandler={() => {
        setErrors({
          ...errors,
          customError: false
        });
        changeState(false);
      }}
      confirmHandler={clickSave}
      title="Edit personal details"
      pending={updating}
    >
      <div className={styles.personalDetails}>
        <Input
          onChange={(e) => setNewData((x) => ({ ...x, FirstName: e.target.value }))}
          defaultValue={FirstName}
          placeholder={t('common.forms.firstname')}
          label={t('common.forms.firstname')}
          type="text"
          error={errors.firstName && validationMessage.INFO_REQ}
        />
        <Input
          onChange={(e) => setNewData((x) => ({ ...x, LastName: e.target.value }))}
          defaultValue={LastName}
          placeholder={t('common.forms.lastname')}
          label={t('common.forms.lastname')}
          type="text"
          error={errors.lastName && validationMessage.INFO_REQ}
        />
        <Input
          onChange={(e) => setNewData((x) => ({ ...x, SecondaryEmail: e.target.value }))}
          defaultValue={SecondaryEmail}
          placeholder={t('common.forms.primaryplaceholder')}
          label={t('common.forms.primaryplaceholder')}
          type="email"
          error={errors.email && validationMessage.EMAIL_INVALID}
        />
        <div className="d-flex flex-column">
          <PhoneNumberInput
            phone={phone}
            onChange={(newPhone, code) => {
              setPhone(newPhone);
              setNewData((x) => ({
                ...x,
                Phone: `${newPhone}`.replace(new RegExp(`${code.dialCode}`), ''),
                PhoneCountryCode: `${code.dialCode ? '+' : ''}${code.dialCode}`
              }));
            }}
            error={errors.phone && validationMessage.PHONE_NUMBER_REQ}
          />
          {newData.Phone?.length !== 0 && newData.Phone?.length <= minSymbolQTY.PHONE && (
            <ValidationErrorLabel message={validationMessage.PHONE_NUMBER_INVALID} />
          )}
        </div>
        <Select
          value={newData.PhoneType || phoneTypes[0].value}
          onChange={(e) => setNewData((x) => ({ ...x, PhoneType: e.target.value }))}
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: 0,
              border: 'none',
              borderBottom: `1px solid ${colors.LIGHT_GREY}`
            }
          }}
          MenuProps={{
            style: { zIndex: 1610 }
          }}
        >
          {phoneTypes.map((type) => (
            <MenuItem key={type.id} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      <AddressInput
        userData={data}
        updateUserData={setNewData}
        edit
        errors={
          (errors.country || errors.city) && {
            country: errors.country && validationMessage.COUNTRY_REQ,
            city: errors.city && validationMessage.CITY_REQ
          }
        }
      />
      {errors.customError && <ValidationErrorLabel message={errors.customError} />}
    </BaseModal>
  );
}

export default EditPersonalDetails;
